import './RecommendedActionTemplates.scss';

import { ActionTemplateCard } from './ActionTemplateCard';
import { createAsyncSelectors } from '@lib/selectors';
import { LoadingSpinner } from '@ui/LoadingSpinner';

import { Alert, Button, Flex, Row } from 'antd';
import { COPY_GENERIC, COPY_SPECIFIC, FULL_ROUTES } from '@sonar-software/sonar-ts-constants';
import {
	coreActions,
	FeatureFlag,
	getRecommendedActionTemplateById,
	postRecommendedActionTemplateByUrl,
	PostRecommendedActionTemplateByUrlResponse,
	RecommendedActionTemplateDetails,
	RecommendedActionTemplateType
} from '@sonar-software/sonar-core';
import { Link, useNavigate } from 'react-router-dom';
import React, { useCallback } from 'react';
import { useAction, useAppNotifications, useAsyncState, useFeatureFlag, useOnMount } from '@hooks';

const selectors = createAsyncSelectors((state) => state.core.dataSecurity.actionTemplates.get);

export interface RecommendedActionTemplatesProps {
	onClick: (templateDetails: RecommendedActionTemplateDetails) => void;
}

async function getTemplateDetails(templateId: number): Promise<RecommendedActionTemplateDetails | undefined> {
	const result = await getRecommendedActionTemplateById({
		templateId
	});

	return result.data;
};

async function postRecommendedActionTemplate(url: string):
	Promise<PostRecommendedActionTemplateByUrlResponse | undefined> {
	const result = await postRecommendedActionTemplateByUrl({ url });

	return result.data;
};

export const RecommendedActionTemplates: React.FC<RecommendedActionTemplatesProps> = ({
	onClick
}) => {
	const isEnabled = useFeatureFlag(FeatureFlag.RecommendedActionTemplates);
	const load = useAction(coreActions.requestGetCoreDataSecurityActionTemplates);
	const requestFollowed = useAction(coreActions.requestGetCoreFollowedAll);
	const navigate = useNavigate();
	const { errorNotification, successNotification } = useAppNotifications();

	const { data, isRequested } = useAsyncState(selectors);

	useOnMount(() => {
		if (isEnabled) {
			load();
		}
	});

	const handleOnClick = useCallback(async(templateId: number) => {
		const templateDetails = await getTemplateDetails(templateId);

		if (!templateDetails) {
			return;
		};

		if (templateDetails.type === RecommendedActionTemplateType.Monitor) {
			onClick(templateDetails);

			return;
		}

		if (templateDetails?.configuration.actionConfig) {
			const response = await postRecommendedActionTemplate(templateDetails.configuration.actionConfig as string);

			if (response) {
				requestFollowed();
				navigate(`${FULL_ROUTES.DATA_DICTIONARY}/${response.url}`);
				successNotification(COPY_SPECIFIC.SENSITIVE_FIELDS_FOLLOWED_MESSAGE(response.fieldCount), {
					message: COPY_SPECIFIC.SENSITIVE_FIELDS_FOLLOWED
				});

				return;
			}
		}

		errorNotification(COPY_GENERIC.CONTACT_SUPPORT);

	}, [ errorNotification, navigate, onClick, requestFollowed, successNotification ]);

	if (!isEnabled) {
		return null;
	}

	return (
		<div className='recommended-action-templates-section'>
			<div className='h2 title'>{ COPY_SPECIFIC.RECOMMENDED_ACTION_TEMPLATES }</div>
			<Alert
				closable
				message={
					<Flex justify='space-between'>
						{ COPY_SPECIFIC.SETUP_CRITICAL_APPLICATIONS_MESSAGE }
						<Link to={ FULL_ROUTES.DATA_SECURITY_APPLICATION_SETUP }>
							<Button
								size='small'
								type='primary'>{ COPY_SPECIFIC.SETUP_CRITICAL_APPLICATIONS }
							</Button>
						</Link>
					</Flex>
				}
				showIcon
				type='info' />
			<LoadingSpinner spinning={ isRequested }>
				<Row
					className='recommended-action-templates'
					wrap>
					{ data?.map((entry) =>
						<ActionTemplateCard
							key={ entry.id }
							onClick={ handleOnClick }
							{ ...entry } />) }
				</Row>
			</LoadingSpinner>
		</div>
	);
};
